import { useState } from 'react';
import { Title } from 'react-admin';
import { useQuery } from '@apollo/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';

import { GET_PROVIDER_COMPARISON } from './payment.queries';
import {
  ProviderComparison as ProviderComparisonData,
  ProviderStats,
  formatAmount,
  formatPercentage,
  PROVIDER_LABELS,
  PROVIDER_COLORS,
} from './payment.types';

/**
 * Date range options
 */
type DateRange = '7d' | '30d' | '90d' | 'year';

function getDateRange(range: DateRange): { startDate: Date; endDate: Date } {
  const endDate = new Date();
  const startDate = new Date();

  switch (range) {
    case '7d':
      startDate.setDate(endDate.getDate() - 7);
      break;
    case '30d':
      startDate.setDate(endDate.getDate() - 30);
      break;
    case '90d':
      startDate.setDate(endDate.getDate() - 90);
      break;
    case 'year':
      startDate.setFullYear(endDate.getFullYear() - 1);
      break;
  }

  return { startDate, endDate };
}

/**
 * Provider stats card
 */
function ProviderStatsCard({ stats, maxVolume }: { stats: ProviderStats; maxVolume: number }) {
  const color = PROVIDER_COLORS[stats.provider];
  const volumePercent = (stats.totalVolume / maxVolume) * 100;

  return (
    <Card
      sx={{
        height: '100%',
        borderLeft: 4,
        borderColor: color,
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            {PROVIDER_LABELS[stats.provider]}
          </Typography>
          <Chip
            label={`${formatPercentage(stats.successRate)} succès`}
            size="small"
            sx={{ backgroundColor: `${color}20`, color }}
          />
        </Box>

        <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
          {formatAmount(stats.totalVolume)}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Part du volume total
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {formatPercentage(volumePercent)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={volumePercent}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: `${color}20`,
              '& .MuiLinearProgress-bar': {
                backgroundColor: color,
                borderRadius: 4,
              },
            }}
          />
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Transactions
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {stats.totalTransactions.toLocaleString('fr-FR')}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Réussies
            </Typography>
            <Typography variant="subtitle1" fontWeight={600} color="success.main">
              {stats.successfulTransactions.toLocaleString('fr-FR')}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Échouées
            </Typography>
            <Typography variant="subtitle1" fontWeight={600} color="error.main">
              {stats.failedTransactions.toLocaleString('fr-FR')}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Frais moyens
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {formatAmount(stats.fees)}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

/**
 * Provider Comparison Component
 */
export function ProviderComparisonPage() {
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const { startDate, endDate } = getDateRange(dateRange);

  const { data, loading } = useQuery<{ providerComparison: ProviderComparisonData }>(GET_PROVIDER_COMPARISON, {
    variables: {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    },
  });

  const comparison = data?.providerComparison;
  const providers = comparison?.providers || [];
  const dailyData = comparison?.dailyComparison || [];

  // Calculate max volume for percentage bars
  const maxVolume = Math.max(...providers.map((p) => p.totalVolume), 1);

  // Prepare radar chart data
  const radarData = [
    {
      metric: 'Volume',
      ...providers.reduce((acc, p) => {
        acc[p.provider] = (p.totalVolume / maxVolume) * 100;
        return acc;
      }, {} as Record<string, number>),
    },
    {
      metric: 'Transactions',
      ...providers.reduce((acc, p) => {
        const maxTx = Math.max(...providers.map((pr) => pr.totalTransactions), 1);
        acc[p.provider] = (p.totalTransactions / maxTx) * 100;
        return acc;
      }, {} as Record<string, number>),
    },
    {
      metric: 'Taux succès',
      ...providers.reduce((acc, p) => {
        acc[p.provider] = p.successRate;
        return acc;
      }, {} as Record<string, number>),
    },
    {
      metric: 'Fiabilité',
      ...providers.reduce((acc, p) => {
        acc[p.provider] = 100 - (p.failedTransactions / Math.max(p.totalTransactions, 1)) * 100;
        return acc;
      }, {} as Record<string, number>),
    },
  ];

  // Format daily comparison data for charts
  const formattedDailyData = dailyData.map((day) => ({
    date: new Date(day.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
    CinetPay: day.cinetpay.volume,
    FedaPay: day.fedapay.volume,
    'EKAA Wallet': day.wallet.volume,
  }));

  return (
    <Box>
      <Title title="Comparaison des providers" />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Comparaison des providers de paiement
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Période</InputLabel>
          <Select
            value={dateRange}
            label="Période"
            onChange={(e) => setDateRange(e.target.value as DateRange)}
          >
            <MenuItem value="7d">7 derniers jours</MenuItem>
            <MenuItem value="30d">30 derniers jours</MenuItem>
            <MenuItem value="90d">90 derniers jours</MenuItem>
            <MenuItem value="year">Cette année</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Provider stats cards */}
      {loading ? (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={4} key={i}>
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {providers.map((stats) => (
            <Grid item xs={12} md={4} key={stats.provider}>
              <ProviderStatsCard stats={stats} maxVolume={maxVolume} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Volume comparison line chart */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Évolution du volume par provider
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : formattedDailyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={formattedDailyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`} />
                    <Tooltip
                      formatter={(value: number) => [formatAmount(value), '']}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="CinetPay"
                      stroke={PROVIDER_COLORS.CINETPAY}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="FedaPay"
                      stroke={PROVIDER_COLORS.FEDAPAY}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="EKAA Wallet"
                      stroke={PROVIDER_COLORS.EKAA_WALLET}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    height: 300,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#F3F4F6',
                    borderRadius: 2,
                  }}
                >
                  <Typography color="text.secondary">Aucune donnée disponible</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Radar chart */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Performance globale
              </Typography>
              {loading ? (
                <Skeleton variant="circular" width={250} height={250} sx={{ mx: 'auto', mt: 2 }} />
              ) : radarData.length > 0 && providers.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="metric" />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} />
                    {providers.map((p) => (
                      <Radar
                        key={p.provider}
                        name={PROVIDER_LABELS[p.provider]}
                        dataKey={p.provider}
                        stroke={PROVIDER_COLORS[p.provider]}
                        fill={PROVIDER_COLORS[p.provider]}
                        fillOpacity={0.2}
                      />
                    ))}
                    <Legend />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    height: 300,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#F3F4F6',
                    borderRadius: 2,
                  }}
                >
                  <Typography color="text.secondary">Aucune donnée disponible</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Bar chart - transactions by provider */}
        <Grid item xs={12}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Transactions quotidiennes par provider
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : formattedDailyData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={formattedDailyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`} />
                    <Tooltip
                      formatter={(value: number) => [formatAmount(value), '']}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Legend />
                    <Bar dataKey="CinetPay" fill={PROVIDER_COLORS.CINETPAY} />
                    <Bar dataKey="FedaPay" fill={PROVIDER_COLORS.FEDAPAY} />
                    <Bar dataKey="EKAA Wallet" fill={PROVIDER_COLORS.EKAA_WALLET} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    height: 300,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#F3F4F6',
                    borderRadius: 2,
                  }}
                >
                  <Typography color="text.secondary">Aucune donnée disponible</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed comparison table */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Comparaison détaillée
          </Typography>
          {loading ? (
            <Skeleton variant="rectangular" height={200} />
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Métrique</TableCell>
                    {providers.map((p) => (
                      <TableCell key={p.provider} align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: PROVIDER_COLORS[p.provider],
                            }}
                          />
                          {PROVIDER_LABELS[p.provider]}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Volume total</TableCell>
                    {providers.map((p) => (
                      <TableCell key={p.provider} align="right">
                        <Typography fontWeight={600}>{formatAmount(p.totalVolume)}</Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Transactions totales</TableCell>
                    {providers.map((p) => (
                      <TableCell key={p.provider} align="right">
                        {p.totalTransactions.toLocaleString('fr-FR')}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Transactions réussies</TableCell>
                    {providers.map((p) => (
                      <TableCell key={p.provider} align="right">
                        <Typography color="success.main">
                          {p.successfulTransactions.toLocaleString('fr-FR')}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Transactions échouées</TableCell>
                    {providers.map((p) => (
                      <TableCell key={p.provider} align="right">
                        <Typography color="error.main">
                          {p.failedTransactions.toLocaleString('fr-FR')}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Taux de succès</TableCell>
                    {providers.map((p) => (
                      <TableCell key={p.provider} align="right">
                        <Chip
                          label={formatPercentage(p.successRate)}
                          size="small"
                          color={p.successRate >= 95 ? 'success' : p.successRate >= 90 ? 'warning' : 'error'}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Temps moyen de traitement</TableCell>
                    {providers.map((p) => (
                      <TableCell key={p.provider} align="right">
                        {p.averageProcessingTime.toFixed(1)}s
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell>Frais totaux</TableCell>
                    {providers.map((p) => (
                      <TableCell key={p.provider} align="right">
                        {formatAmount(p.fees)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default ProviderComparisonPage;
